import React, {useMemo, useState} from 'react';
import {Box, Divider, MenuItem, MenuList, Popover} from "@mui/material";
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import {useNavigate} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {useAppContext} from "@context/appContext/hooks/useAppContext";
import {useGetRelatedAccountsQuery} from "@containers/account/hooks/useAccountsQuery";
import {useAccountIdSingleVoyage} from "@containers/singleVoyage/account/hooks/useAccountIdSingleVoyage";
import ButtonCustom from "@ui/buttons/buttonCustom/ButtonCustom";
import InputSearch from "@ui/inputs/InputSearch";
import {sx} from './sx'

interface IProps {
    navigateTo?: string
    fullWidth?: boolean
}

const AccountSwitcherSingleVoyage = ({navigateTo, fullWidth}: IProps) => {

    const {t} = useTranslation()
    const navigate = useNavigate()
    const {isMultiAccount, account} = useAppContext()
    const {accountId, setAccountId} = useAccountIdSingleVoyage()

    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
    const [search, setSearch] = useState<string>('')

    const {data} = useGetRelatedAccountsQuery({
        params: {
            accountId: account?.id,
            featureFlag: 'SingleVoyage'
        },
        enabled: !!isMultiAccount
    })

    const ownAccountId = account?.id?.toString()
    const ownLabel = account?.labelName || account?.code?.toString() || ''

    const carriers = useMemo(() => {
        return (data ?? []).map((item) => ({
            id: item.id?.toString(),
            label: item.labelName ? item.labelName : item.code?.toString(),
        }))
    }, [data])

    const filteredCarriers = useMemo(() => {
        const query = search.trim().toLowerCase()
        if (!query) {
            return carriers
        }
        return carriers.filter((item) => item.label?.toLowerCase().includes(query))
    }, [carriers, search])

    const currentLabel = useMemo(() => {
        if (accountId === ownAccountId) {
            return ownLabel
        }
        return carriers.find((item) => item.id === accountId)?.label ?? accountId
    }, [accountId, ownAccountId, ownLabel, carriers])

    if (!isMultiAccount) {
        return null
    }

    const close = () => {
        setAnchorEl(null)
        setSearch('')
    }

    const select = (id: string) => {
        close()
        if (navigateTo) {
            navigate(`${navigateTo}?accountId=${id}`)
            return
        }
        setAccountId(id)
    }

    return (
        <>
            <ButtonCustom
                sx={fullWidth ? {...sx.button, ...sx.buttonFullWidth} : sx.button}
                fullWidth={fullWidth}
                onClick={(event) => setAnchorEl(event.currentTarget as HTMLElement)}
                endIcon={<ArrowDropDownIcon fontSize={'small'}/>}
                variant={'contained'}
                size={'small'}
            >
                <Box component={'span'} sx={sx.label}>{currentLabel}</Box>
            </ButtonCustom>

            <Popover
                open={!!anchorEl}
                anchorEl={anchorEl}
                onClose={close}
                anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
                transformOrigin={{vertical: 'top', horizontal: 'left'}}
                slotProps={{paper: {sx: sx.paper}}}
            >
                <InputSearch
                    value={search}
                    onChange={setSearch}
                    placeholder={t('base.search')}
                    showSearchIcon
                    fullWidth
                    size={'small'}
                    autoFocus
                />
                <MenuList sx={sx.list} dense>
                    <MenuItem
                        selected={accountId === ownAccountId}
                        onClick={() => select(ownAccountId)}
                        sx={{...sx.item, ...sx.ownItem}}
                    >
                        {ownLabel}
                    </MenuItem>
                    <Divider/>
                    {filteredCarriers.map((item) => (
                        <MenuItem
                            key={item.id}
                            selected={accountId === item.id}
                            onClick={() => select(item.id)}
                            sx={sx.item}
                        >
                            {item.label}
                        </MenuItem>
                    ))}
                </MenuList>
            </Popover>
        </>
    );
};

export default AccountSwitcherSingleVoyage;
